import React, { useEffect } from 'react';
import { styled } from '@material-ui/core/styles';
import { Typography, Button } from '@material-ui/core';
import CallEndIcon from '@material-ui/icons/CallEnd';

/**
 * Message type sent to parent window (messenger) when call ends.
 * See docs/CALL_ENDED_NOTIFICATION.md for the full flow.
 */
export const VIDEO_CALL_ENDED_MESSAGE = 'VIDEO_CALL_ENDED';

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
});

const IconContainer = styled('div')({
  width: 80,
  height: 80,
  borderRadius: '50%',
  backgroundColor: 'rgba(244, 67, 54, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 24,
});

const StyledCallEndIcon = styled(CallEndIcon)({
  fontSize: 40,
  color: '#f44336',
});

const Message = styled(Typography)({
  color: '#ffffff',
  marginBottom: 8,
});

const SubMessage = styled(Typography)({
  color: 'rgba(255, 255, 255, 0.6)',
  marginBottom: 32,
  textAlign: 'center',
});

const CloseButton = styled(Button)({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
});

interface CallEndedProps {
  // The guest was disconnected but the call is still going: offer to rejoin with the same link.
  canRejoin?: boolean;
}

export default function CallEnded({ canRejoin = false }: CallEndedProps) {
  // window.close() only works for windows opened by scripts (window.opener is set)
  const canClose = window.opener !== null;

  // Notify parent window (messenger) that the call has ended
  // See docs/CALL_ENDED_NOTIFICATION.md for security details
  useEffect(() => {
    if (window.opener) {
      // Get the call token passed by messenger when opening this window
      const params = new URLSearchParams(window.location.search);
      const callToken = params.get('callToken');

      // Send to specific origin (configured per environment) instead of '*'
      // Fallback to '*' only for backwards compatibility during migration
      const targetOrigin = process.env.REACT_APP_MESSENGER_ORIGIN || '*';

      window.opener.postMessage(
        { type: VIDEO_CALL_ENDED_MESSAGE, callToken },
        targetOrigin
      );
    }
  }, []);

  return (
    <Container>
      <IconContainer>
        <StyledCallEndIcon />
      </IconContainer>
      <Message variant="h5">Call Ended</Message>
      <SubMessage variant="body1">
        {canRejoin
          ? 'You were disconnected. The call may still be going.'
          : canClose
          ? 'You can close this window now.'
          : 'You may now close this page.'}
      </SubMessage>
      {canRejoin && (
        <CloseButton variant="contained" onClick={() => window.location.reload()}>
          Rejoin Call
        </CloseButton>
      )}
      {canClose && (
        <CloseButton variant="contained" onClick={() => window.close()}>
          Close Window
        </CloseButton>
      )}
    </Container>
  );
}
